import type { ChargeStatus, ConversionTrackingView } from './campaignPerformanceTypes'
import type { ReservationPeriod } from './reservationViewModel'
import { validateReservationPeriod } from './reservationViewModel'

/**
 * 홍보 성과·비용 조회 화면 공통 순수 함수(설계 §2, #738) — 필터 상태·쿼리 파라미터·CSV 파일명·
 * null 표시 규칙. `campaignViewModel.ts`(발송 흐름·전 상태 이력, 작성일 축)와는 축이 달라 병렬
 * 파일로 둔다(`#736` §1 — 발송 상태 축과 과금 상태 축은 서로 다른 EP가 소유, 한 화면에 섞지 않는다).
 */

export interface PerformanceListFilter extends ReservationPeriod {
  /** `undefined` = 전체(필터 미적용). */
  chargeStatus?: ChargeStatus
}

/** #736 — 62일 상한이 기존 예약/이력 조회와 동일해 새 검증 함수를 만들지 않고 그대로 재사용한다. */
export const validatePerformancePeriod = validateReservationPeriod

/** 기본 조회 기간 — 이번 달(1일 ~ 오늘). 관리 홈 요약(`defaultCampaignSummaryPeriod`)과 같은 관례. */
export function defaultPerformancePeriod(today: string): ReservationPeriod {
  return { from: `${today.slice(0, 7)}-01`, to: today }
}

export const CHARGE_STATUS_FILTER_OPTIONS: { value: ChargeStatus; label: string }[] = [
  { value: 'UNSETTLED', label: '미정산' },
  { value: 'SETTLED', label: '정산 완료' },
]

/** 목록·CSV EP 공용 쿼리 파라미터 — `undefined` 필드는 axios가 자동으로 뺀다. */
export function performanceQueryParams(
  filter: PerformanceListFilter,
  cursor?: string
): Record<string, string | undefined> {
  return {
    from: filter.from,
    to: filter.to,
    chargeStatus: filter.chargeStatus,
    cursor,
  }
}

/** CSV 파일명 — api-spec(#734 §1-4) 패턴을 그대로 재현한다. */
export function performanceExportFileName(venueId: string, from: string, to: string): string {
  const compact = (date: string) => date.replaceAll('-', '')
  return `venue-${venueId}_promotion-performance_${compact(from)}_${compact(to)}.csv`
}

/** 대상 지역 표시 — 이름이 없으면(행정구역 통폐합 등) 코드를 그대로(#734 §1-3). */
export function regionDisplayText(item: {
  regionStateId: string
  regionStateName: string | null
  regionCityId: string | null
  regionCityName: string | null
}): string {
  const state = item.regionStateName ?? item.regionStateId
  if (!item.regionCityId) return state
  const city = item.regionCityName ?? item.regionCityId
  return `${state} ${city}`
}

/**
 * 전환 지표 안내 문구 — `conversionTracking.enabled=false`면 숫자 대신 이 문구를 그린다(#734 §0).
 * `enabled=true`면 `null`을 돌려줘 호출부가 실측값(건수·율)을 그대로 렌더하게 한다.
 */
export function conversionStatusText(conversionTracking: Pick<ConversionTrackingView, 'enabled'>): string | null {
  return conversionTracking.enabled ? null : '측정 준비 중'
}

/** 과금 상태 표시 — `chargeStatusLabel`이 없으면(과금 미확정) "확정 대기"(🚫 "0원"이 아니다, #734 §1-3). */
export function chargeStatusDisplayText(item: { chargeStatusLabel: string | null }): string {
  return item.chargeStatusLabel ?? '확정 대기'
}
